import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, Grid, Typography } from "@material-ui/core";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import FormCheckbox from "../../../../../../../components/molecules/checkbox";
import { useTranslation } from "react-i18next";
import Config from "../../../../../../../config";
import { isEmpty, sortBy } from "lodash";
import { Skeleton } from "@material-ui/lab";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoadingCheckoutSession,
  setSelectedPaymentMethod,
} from "../../../../../../../store/actions/pay-for-ad";
import {
  PAYMENT_GATEWAYS,
  PAYMENT_METHOD_IDS,
  PAYMENT_OPTIONS,
  PAYMENT_TYPES,
  PROFILE_STATUS,
  SUPPORTED_CURRENCIES,
} from "../../../../../../../config/constant";
import { createPayementSession } from "../../../../../../../features/payment";
import PaymentMethodCard from "../payment-method-card";
import moment from "moment";
import "../../../../style.css";

const {
  DOMAIN_DISPLAY_NAME,
  TARGET_ENVIRONMENT,
  TARGET_ENVIRONMENTS,
  STRIPE_KEY,
  API_URL,
  EXCLUDED_PAYMENT_METHODS,
} = Config;
const renewalProfileStatus = [PROFILE_STATUS.EXPIRED, PROFILE_STATUS.LIVE];

const FilteredPaymentOptions = ({
  selectedMethod,
  setDisplayFilteredOptions,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { authProfile } = useSelector((state) => state.application);
  const selectedCurrency = sessionStorage.getItem("checkout_currency");
  const recurringPayment =
    sessionStorage.getItem(PAYMENT_TYPES.IS_RECURRING) === "true";

  const {
    loadingInvoiceData,
    selectedPackageId,
    selectedPaymentMethod,
    loadingCheckoutSession,
    promoCode,
    paymentMethods,
    paymentSession,
  } = useSelector((state) => state.payForAd);

  const [payhereSession, setPayhereSession] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [checkoutError, setCheckoutError] = useState();

  const filteredPaymentMethods = useMemo(() => {
    let output = [];

    switch (selectedMethod) {
      case PAYMENT_OPTIONS.ONLINE_PAYMENT:
        if (selectedCurrency === SUPPORTED_CURRENCIES.USD && recurringPayment) {
          output = paymentMethods.filter(
            (el) => el.id === PAYMENT_METHOD_IDS.FOREIGN_CC
          );
        } else {
          output = sortBy(
            paymentMethods.filter(
              (el) =>
                el.id === PAYMENT_METHOD_IDS.FOREIGN_CC ||
                (selectedCurrency !== SUPPORTED_CURRENCIES.USD &&
                  el.id === PAYMENT_METHOD_IDS.LOCAL_CC) ||
                (!recurringPayment && el.id === PAYMENT_METHOD_IDS.PAYPAL)
            ),
            ["sortOrder"]
          );
        }
        break;
      case PAYMENT_OPTIONS.OTHER:
        output = paymentMethods.filter(
          (el) =>
            !el.isBank &&
            el.region === "LK" &&
            el.id !== PAYMENT_METHOD_IDS.LOCAL_CC
        );
        break;
      case PAYMENT_OPTIONS.BANK_TRANSFER:
        output = sortBy(
          paymentMethods.filter((el) => el.isBank),
          (element) => {
            return element.region !== "LK";
          }
        );
        break;
      default:
        break;
    }

    return output.filter((el) => !EXCLUDED_PAYMENT_METHODS.includes(el.id));
  }, [selectedMethod, paymentMethods, selectedCurrency, recurringPayment]);

  const getPaymentParameters = useCallback(
    ({ paymentMethodId }) => {
      const isRenewAccount = renewalProfileStatus.includes(authProfile.status);
      let data = {
        ...(promoCode && { promoCode: promoCode.trim() }),
        ...(recurringPayment && { isRecurring: recurringPayment }),
        ...(paymentMethodId && { paymentMethodId: paymentMethodId }),
        isRenew: isRenewAccount,
        forceLKR: selectedCurrency !== SUPPORTED_CURRENCIES.USD,
        pricingTierId: selectedPackageId,
        simulate: false,
      };
      return data;
    },
    [
      authProfile,
      promoCode,
      recurringPayment,
      selectedPackageId,
      selectedCurrency,
    ]
  );

  const handlePayherePayment = (session) => {
    setPayhereSession(session.payhereParams);
    if (selectedCurrency === SUPPORTED_CURRENCIES.LKR) {
      sessionStorage.setItem("force_lkr", true);
    } else {
      sessionStorage.removeItem("force_lkr", false);
    }

    sessionStorage.setItem("paying_currency", session.currency);
    sessionStorage.setItem("selected_package", selectedPackageId);
    localStorage.setItem("payment_payhere_reminder", moment().format());
    document.getElementById("pay-by-genie").click();
  };

  const handlePaymentSession = async ({ paymentMethodId, ipg }) => {
    dispatch(setLoadingCheckoutSession(true));
    try {
      const response = await createPayementSession({
        postId: authProfile.id,
        data: getPaymentParameters({ paymentMethodId }),
      });

      if (!response.success) {
        setCheckoutError(t("errors.unknownErrorOccurred"));
        dispatch(setLoadingCheckoutSession(false));
        return;
      }

      switch (response.body.ipg) {
        case PAYMENT_GATEWAYS.STRIPE:
          var stripe = window.Stripe(STRIPE_KEY);
          await stripe
            .redirectToCheckout({
              sessionId: response.body.sessionId,
            })
            .then(() => {
              dispatch(setLoadingCheckoutSession(false));
            });
          break;
        case PAYMENT_GATEWAYS.PAYHERE:
          handlePayherePayment(response.body);
          break;
        default:
          break;
      }
    } catch (error) {
      setCheckoutError(t("errors.unknownErrorOccurred"));
      dispatch(setLoadingCheckoutSession(false));
      console.log("Error", error);
    }
  };

  const onContinue = () => {
    if (isEmpty(selectedPaymentMethod)) {
      setCheckoutError(t("payment.selectPaymentMethodToCotinue"));
      return;
    }
    if (!agreedToTerms) {
      setTermsError(true);
      return;
    }
    if (selectedPaymentMethod.isBank) {
      sessionStorage.setItem("selected_package", selectedPackageId);
      history.push(
        `/checkout/bank-transfer?code=${selectedPaymentMethod.region}`
      );
      return;
    }
    switch (selectedPaymentMethod.id) {
      case PAYMENT_METHOD_IDS.FOREIGN_CC:
        handlePaymentSession({});
        break;
      case PAYMENT_METHOD_IDS.LOCAL_CC:
      case PAYMENT_METHOD_IDS.EZ_CASH:
      case PAYMENT_METHOD_IDS.FRIMI:
      case PAYMENT_METHOD_IDS.GENIE:
      case PAYMENT_METHOD_IDS.M_CASH:
      case PAYMENT_METHOD_IDS.VISHWA:
        handlePaymentSession({ paymentMethodId: PAYMENT_METHOD_IDS.LOCAL_CC });
        break;
      default:
        break;
    }
  };

  const initPaypal = useCallback(() => {
    if (selectedPaymentMethod.id === PAYMENT_METHOD_IDS.PAYPAL) {
      const environment =
        TARGET_ENVIRONMENT === TARGET_ENVIRONMENTS.DEVELOP
          ? "sandbox"
          : "production";
      window.paypal.Button.render(
        {
          env: environment,
          style: {
            size: "responsive",
            shape: "rect",
          },
          validate: function (actions) {
            const element = document.querySelector("#payment-terms-checked");
            if (element) {
              if (!element.checked) {
                actions.disable();
              }
              element.addEventListener("change", function (event) {
                if (event.target.checked) {
                  actions.enable();
                } else {
                  actions.disable();
                }
              });
            } else {
              actions.disable();
            }
          },
          onClick: function () {
            const element = document.querySelector("#payment-terms-checked");
            if (element && !element.checked) {
              setTermsError(true);
            }
          },
          payment: function (data, actions) {
            return actions.request
              .post(
                `${API_URL}/v1/account/posts/${authProfile.id}/checkout_tiered`,
                getPaymentParameters({ paymentMethodId: "paypal" }),
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "auth_token"
                    )}`,
                  },
                }
              )
              .then(function (res) {
                return res.paypalParams.orderId;
              })
              .catch((err) => {
                setCheckoutError(t("errors.unknownErrorOccurred"));
                console.log(err);
              });
          },
          onAuthorize: function (data, actions) {
            return actions.request
              .post(`${API_URL}/v1/account/posts/confirm_checkout_paypal`, {
                paymentId: data.paymentID,
                payerId: data.payerID,
              })
              .then(function (res) {
                window.alert("Payment Complete!");
              });
          },
        },
        "#paypal-button"
      );
    }
  }, [authProfile, getPaymentParameters, t, selectedPaymentMethod]);

  useEffect(() => {
    dispatch(setLoadingCheckoutSession(false));
    initPaypal();
  }, [initPaypal, dispatch]);

  useEffect(() => {
    setCheckoutError("");
  }, [selectedPaymentMethod]);

  useEffect(() => {
    return () => {
      dispatch(setLoadingCheckoutSession(false));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSelectedPaymentMethod({}));
  }, [paymentSession, dispatch]);

  return (
    <Grid container direction="column" className="filtered-methods-view">
      <Grid container direction="row">
        <KeyboardArrowLeftIcon
          onClick={() => setDisplayFilteredOptions(false)}
          className="prev-nav-btn"
        />
        <Typography variant="body1" className="filtered-methods-titile">
          {t("payment.paymentMethods")}
        </Typography>
      </Grid>
      <Grid container direction="column" className="filtered-methods-list-view">
        {isEmpty(filteredPaymentMethods) ? (
          <>
            <Skeleton height={30} />
            <Skeleton animation={false} height={30} />
            <Skeleton animation="wave" height={30} />
          </>
        ) : (
          filteredPaymentMethods.map((element) => (
            <PaymentMethodCard
              id={element.id}
              data={element}
              session={payhereSession}
            />
          ))
        )}
      </Grid>
      {!isEmpty(filteredPaymentMethods) && (
        <FormCheckbox
          id="payment-terms-checked"
          label={t("payment.agreeToTerms", {
            domainName: DOMAIN_DISPLAY_NAME,
          })}
          handleChange={(e) => {
            setTermsError(false);
            setAgreedToTerms(e.target.checked);
          }}
          containerProps={{
            ...(termsError && { border: "1px solid red" }),
          }}
        />
      )}

      {checkoutError && (
        <Typography variant="caption" className="checkout-err-txt">
          {checkoutError}
        </Typography>
      )}

      {selectedPaymentMethod.id === PAYMENT_METHOD_IDS.PAYPAL && (
        <div id="paypal-button" />
      )}

      {!isEmpty(filteredPaymentMethods) &&
        selectedPaymentMethod.id !== PAYMENT_METHOD_IDS.PAYPAL && (
          <Button
            id="make-payment-btn"
            className="payment-continue-btn"
            disabled={loadingInvoiceData || loadingCheckoutSession}
            onClick={onContinue}
          >
            {loadingInvoiceData || loadingCheckoutSession ? (
              <CircularProgress size={20} />
            ) : (
              t("payment.continuePayment")
            )}
          </Button>
        )}
    </Grid>
  );
};

export default FilteredPaymentOptions;
